import { useState, useEffect } from 'react'
import { Plus, Flag, Trophy, XCircle, Calendar, Users, Target, ChevronLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { maskNumber, unmaskNumber } from '../../../utils/numberUtils'
import { toast } from '../../../components/Toast'
import { Button, Badge, Card, Modal, ModalHeader, ModalBody, ModalFooter, EmptyState } from '../../../components/ui'

interface Campaign {
  id: number
  name: string
  election_year: number
  electoral_number: string | null
  scope: string
  status_code: string
  status_label: string
  status_color: string
  position_label: string
  party_abbreviation: string | null
  party_number: number | null
  state_code: string | null
  city_name: string | null
  start_date: string | null
  end_date: string | null
  vote_goal: number | null
  votes_obtained: number | null
  result_code: string | null
  result_label: string | null
  result_color: string | null
  is_active_campaign: boolean
}

interface Position { id: number; code: string; label: string; scope: string }
interface Party { id: number; number: number; abbreviation: string; name: string }
interface StatusType { id: number; code: string; label: string; color: string }

const statusBadge: Record<string, 'brand' | 'success' | 'error' | 'warning' | 'gray' | 'info'> = {
  PLANNING: 'info', ACTIVE: 'brand', SUSPENDED: 'warning', FINISHED: 'gray', CANCELLED: 'gray',
}

interface GeoState { id: number; code: string; name: string }
interface GeoCity { id: number; name: string }

export default function CampaignsPage() {
  const navigate = useNavigate()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [positions, setPositions] = useState<Position[]>([])
  const [parties, setParties] = useState<Party[]>([])
  const [statuses, setStatuses] = useState<StatusType[]>([])
  const [states, setStates] = useState<GeoState[]>([])
  const [cities, setCities] = useState<GeoCity[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [finishModalOpen, setFinishModalOpen] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null)

  // Form
  const [form, setForm] = useState({
    name: '', election_year: new Date().getFullYear().toString(),
    political_position_id: '', party_id: '', electoral_number: '',
    geo_state_id: '', geo_city_id: '',
    start_date: '', end_date: '', vote_goal: '', notes: '',
  })

  // Scope derivado do cargo selecionado
  const selectedPosition = positions.find(p => p.id === parseInt(form.political_position_id))
  const scope = selectedPosition?.scope || ''

  // Finish form
  const [finishForm, setFinishForm] = useState({ result: '', votes_obtained: '' })

  useEffect(() => {
    fetchCampaigns()
    fetchRefs()
  }, [])

  async function fetchCampaigns() {
    setLoading(true)
    try {
      const res = await axios.get(buildApiUrl('/v1/campaigns'))
      setCampaigns(res.data.data)
    } catch { /* */ } finally { setLoading(false) }
  }

  async function fetchRefs() {
    try {
      const [p, pa, st] = await Promise.all([
        axios.get(buildApiUrl('/v1/political/positions')),
        axios.get(buildApiUrl('/v1/political/parties')),
        axios.get(buildApiUrl('/v1/geo/states')),
      ])
      setPositions(p.data.data)
      setParties(pa.data.data)
      setStates(st.data.data)
    } catch { /* */ }
  }

  // Carregar cidades quando seleciona estado
  useEffect(() => {
    if (form.geo_state_id) {
      axios.get(buildApiUrl(`/v1/geo/states/${form.geo_state_id}/cities`))
        .then(res => setCities(res.data.data))
        .catch(() => setCities([]))
    } else {
      setCities([])
    }
    setForm(f => ({ ...f, geo_city_id: '' }))
  }, [form.geo_state_id])

  function openCreate() {
    setSelectedCampaign(null)
    setForm({
      name: '', election_year: new Date().getFullYear().toString(),
      political_position_id: '', party_id: '', electoral_number: '',
      geo_state_id: '', geo_city_id: '',
      start_date: '', end_date: '', vote_goal: '', notes: '',
    })
    setModalOpen(true)
  }

  function openFinish(campaign: Campaign) {
    setSelectedCampaign(campaign)
    setFinishForm({ result: '', votes_obtained: '' })
    setFinishModalOpen(true)
  }

  async function handleSave() {
    if (saving) return
    setSaving(true)
    const payload: any = {
      name: form.name,
      election_year: parseInt(form.election_year),
      political_position_id: parseInt(form.political_position_id),
      party_id: form.party_id ? parseInt(form.party_id) : null,
      electoral_number: form.electoral_number || null,
      scope: scope,
      geo_state_id: form.geo_state_id ? parseInt(form.geo_state_id) : null,
      geo_city_id: form.geo_city_id ? parseInt(form.geo_city_id) : null,
      vote_goal: unmaskNumber(form.vote_goal),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      notes: form.notes || null,
    }

    try {
      await axios.post(buildApiUrl('/v1/campaigns'), payload)
      toast('success', 'Campanha criada')
      setModalOpen(false)
      fetchCampaigns()
    } catch (err: any) {
      toast('error', err.response?.data?.message || 'Erro ao criar campanha')
    } finally { setSaving(false) }
  }

  async function handleActivate(id: number) {
    try {
      await axios.put(buildApiUrl(`/v1/campaigns/${id}/activate`))
      toast('success', 'Modo campanha ativado')
      fetchCampaigns()
    } catch (err: any) {
      toast('error', err.response?.data?.message || 'Erro ao ativar')
    }
  }

  async function handleFinish() {
    if (!selectedCampaign || !finishForm.result) return
    try {
      await axios.put(buildApiUrl(`/v1/campaigns/${selectedCampaign.id}/finish`), {
        result: finishForm.result,
        votes_obtained: unmaskNumber(finishForm.votes_obtained),
      })
      toast('success', finishForm.result === 'VICTORY' ? 'Campanha encerrada com vitória!' : 'Campanha encerrada')
      setFinishModalOpen(false)
      fetchCampaigns()
    } catch (err: any) {
      toast('error', err.response?.data?.message || 'Erro ao encerrar')
    }
  }

  const activeCampaign = campaigns.find(c => c.is_active_campaign)
  const historyCampaigns = campaigns.filter(c => !c.is_active_campaign)

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/settings/general')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Campanhas</h1>
            <p className="text-gray-500 mt-1">Gerencie campanhas eleitorais</p>
          </div>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={openCreate}>Nova Campanha</Button>
      </div>

      {/* Campanha ativa */}
      {activeCampaign && (
        <div className="bg-brand-950 rounded-2xl p-6 text-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Flag className="w-5 h-5 text-brand-300" />
                <span className="text-sm font-medium text-brand-300">Campanha Ativa</span>
              </div>
              <h2 className="text-xl font-bold mb-1">{activeCampaign.name}</h2>
              <p className="text-white/60 text-sm">
                {activeCampaign.position_label}
                {activeCampaign.party_abbreviation && ` - ${activeCampaign.party_abbreviation} (${activeCampaign.party_number})`}
                {activeCampaign.electoral_number && ` - Nº ${activeCampaign.electoral_number}`}
              </p>
            </div>
            <button onClick={() => openFinish(activeCampaign)}
              className="h-9 px-4 rounded-lg border border-white/20 text-sm font-medium text-white hover:bg-white/10 transition-colors">
              Encerrar
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider">Ano</p>
              <p className="text-lg font-semibold mt-1">{activeCampaign.election_year}</p>
            </div>
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider">Abrangência</p>
              <p className="text-lg font-semibold mt-1">
                {activeCampaign.scope === 'NATIONAL' ? 'Brasil' : activeCampaign.scope === 'STATE' ? activeCampaign.state_code : activeCampaign.city_name}
              </p>
            </div>
            {activeCampaign.vote_goal && (
              <div>
                <p className="text-xs text-white/40 uppercase tracking-wider">Meta de votos</p>
                <p className="text-lg font-semibold mt-1">{activeCampaign.vote_goal.toLocaleString('pt-BR')}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider">Período</p>
              <p className="text-sm font-medium mt-1 text-white/70">
                {activeCampaign.start_date ? new Date(activeCampaign.start_date + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}
                {' até '}
                {activeCampaign.end_date ? new Date(activeCampaign.end_date + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sem campanha ativa */}
      {!activeCampaign && !loading && campaigns.length === 0 && (
        <Card>
          <EmptyState
            icon={<Flag className="w-7 h-7" />}
            title="Nenhuma campanha"
            description="Crie sua primeira campanha eleitoral para ativar o modo campanha."
            action={<Button size="sm" onClick={openCreate} icon={<Plus className="w-4 h-4" />}>Criar Campanha</Button>}
          />
        </Card>
      )}

      {/* Histórico */}
      {historyCampaigns.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Histórico</h3>
          <div className="space-y-3">
            {historyCampaigns.map(c => (
              <div key={c.id} className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-gray-900">{c.name}</h4>
                      <Badge variant={statusBadge[c.status_code] || 'gray'} dot size="sm">{c.status_label}</Badge>
                      {c.result_code && (
                        <Badge variant={c.result_code === 'VICTORY' ? 'success' : 'error'} size="sm">
                          {c.result_code === 'VICTORY' ? <Trophy className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                          {c.result_label}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mt-1">
                      <span>{c.position_label}</span>
                      {c.party_abbreviation && <span>{c.party_abbreviation} ({c.party_number})</span>}
                      <span>{c.election_year}</span>
                      {c.votes_obtained && <span>{c.votes_obtained.toLocaleString('pt-BR')} votos</span>}
                      {c.vote_goal && <span>Meta: {c.vote_goal.toLocaleString('pt-BR')}</span>}
                    </div>
                  </div>
                  {c.status_code === 'PLANNING' && !activeCampaign && (
                    <Button size="sm" variant="outline" onClick={() => handleActivate(c.id)}>Ativar</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal criar */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="lg">
        <ModalHeader onClose={() => setModalOpen(false)}>Nova Campanha</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome da campanha</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Campanha 2026"
                  className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Ano da eleição</label>
                <input type="number" value={form.election_year} onChange={e => setForm({ ...form, election_year: e.target.value })}
                  className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Cargo pretendido</label>
              <select value={form.political_position_id} onChange={e => setForm({ ...form, political_position_id: e.target.value, geo_state_id: '', geo_city_id: '' })}
                className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none appearance-none">
                <option value="">Selecione o cargo</option>
                {positions.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>

            {/* Abrangência - baseado no scope do cargo */}
            {scope === 'NATIONAL' && (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-500">Abrangência: <span className="font-medium text-gray-800">Brasil (Nacional)</span></p>
              </div>
            )}

            {scope === 'STATE' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Estado</label>
                <select value={form.geo_state_id} onChange={e => setForm({ ...form, geo_state_id: e.target.value })}
                  className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none appearance-none">
                  <option value="">Selecione o estado</option>
                  {states.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
                </select>
              </div>
            )}

            {scope === 'MUNICIPAL' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Estado</label>
                  <select value={form.geo_state_id} onChange={e => setForm({ ...form, geo_state_id: e.target.value })}
                    className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none appearance-none">
                    <option value="">Selecione o estado</option>
                    {states.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Município</label>
                  <select value={form.geo_city_id} onChange={e => setForm({ ...form, geo_city_id: e.target.value })}
                    disabled={!form.geo_state_id}
                    className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none appearance-none disabled:opacity-50">
                    <option value="">{form.geo_state_id ? 'Selecione o município' : 'Selecione o estado primeiro'}</option>
                    {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Partido</label>
                <select value={form.party_id} onChange={e => setForm({ ...form, party_id: e.target.value })}
                  className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none appearance-none">
                  <option value="">Selecione (opcional)</option>
                  {parties.map(p => <option key={p.id} value={p.id}>{p.abbreviation} - {p.number}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Número de urna</label>
                <input value={form.electoral_number} onChange={e => setForm({ ...form, electoral_number: e.target.value })} placeholder="Ex: 22123"
                  className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Data início</label>
                <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })}
                  className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Data fim</label>
                <input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })}
                  className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Meta de votos</label>
                <input type="text" inputMode="numeric" value={form.vote_goal} onChange={e => setForm({ ...form, vote_goal: maskNumber(e.target.value) })} placeholder="50.000"
                  className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={!form.name || !form.political_position_id} loading={saving}>Criar Campanha</Button>
        </ModalFooter>
      </Modal>

      {/* Modal encerrar */}
      <Modal open={finishModalOpen} onClose={() => setFinishModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setFinishModalOpen(false)}>Encerrar Campanha</ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-600 mb-5">Como foi o resultado da campanha <strong>"{selectedCampaign?.name}"</strong>?</p>

          <div className="grid grid-cols-2 gap-3 mb-5">
            <button
              onClick={() => setFinishForm({ ...finishForm, result: 'VICTORY' })}
              className={clsx('flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-colors', finishForm.result === 'VICTORY' ? 'border-success-500 bg-success-50' : 'border-gray-200 hover:border-gray-300')}
            >
              <Trophy className={clsx('w-8 h-8', finishForm.result === 'VICTORY' ? 'text-success-500' : 'text-gray-400')} />
              <span className={clsx('text-sm font-semibold', finishForm.result === 'VICTORY' ? 'text-success-700' : 'text-gray-600')}>Vitória</span>
            </button>
            <button
              onClick={() => setFinishForm({ ...finishForm, result: 'DEFEAT' })}
              className={clsx('flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-colors', finishForm.result === 'DEFEAT' ? 'border-error-500 bg-error-50' : 'border-gray-200 hover:border-gray-300')}
            >
              <XCircle className={clsx('w-8 h-8', finishForm.result === 'DEFEAT' ? 'text-error-500' : 'text-gray-400')} />
              <span className={clsx('text-sm font-semibold', finishForm.result === 'DEFEAT' ? 'text-error-700' : 'text-gray-600')}>Derrota</span>
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Votos obtidos</label>
            <input type="text" inputMode="numeric" value={finishForm.votes_obtained} onChange={e => setFinishForm({ ...finishForm, votes_obtained: maskNumber(e.target.value) })} placeholder="50.000"
              className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setFinishModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleFinish} disabled={!finishForm.result}>Encerrar Campanha</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
